import { createContext, useContext, useState, useCallback } from "react";
import { Dialog, DialogContent, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { submitEnquiry } from "@/lib/api";
import { toast } from "sonner";
import { motion, AnimatePresence } from "framer-motion";
import { Check, Loader2 } from "lucide-react";

const EnquiryContext = createContext(null);

export const useEnquiry = () => {
  const ctx = useContext(EnquiryContext);
  if (!ctx) throw new Error("useEnquiry must be used within EnquiryProvider");
  return ctx;
};

const emptyForm = { name: "", email: "", phone: "", message: "" };

export function EnquiryProvider({ children }) {
  const [open, setOpen] = useState(false);
  const [artwork, setArtwork] = useState(null);
  const [form, setForm] = useState(emptyForm);
  const [status, setStatus] = useState("idle"); // idle | sending | done

  const openEnquiry = useCallback((art = null) => {
    setArtwork(art);
    setForm(emptyForm);
    setStatus("idle");
    setOpen(true);
  }, []);

  const change = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const validEmail = (v) => /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(v);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!form.name.trim()) return toast.error("Please enter your name.");
    if (!validEmail(form.email)) return toast.error("Please enter a valid email.");
    if (!form.message.trim()) return toast.error("Please add a short message.");
    setStatus("sending");
    try {
      await submitEnquiry({
        name: form.name.trim(),
        email: form.email.trim(),
        phone: form.phone.trim(),
        artwork_slug: artwork?.slug || "",
        artwork_title: artwork?.title || "",
        message: form.message.trim(),
      });
      setStatus("done");
      toast.success("Enquiry sent \u2014 the studio will be in touch soon.");
    } catch (err) {
      setStatus("idle");
      toast.error("Something went wrong. Please try again.");
    }
  };

  return (
    <EnquiryContext.Provider value={{ openEnquiry }}>
      {children}
      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent
          className="max-w-3xl w-[94vw] p-0 gap-0 border border-ink-soft/25 bg-paper text-ink overflow-hidden"
          data-testid="enquiry-dialog"
        >
          <DialogTitle className="sr-only">
            {artwork ? `Enquire about ${artwork.title}` : "Studio enquiry"}
          </DialogTitle>
          <DialogDescription className="sr-only">
            Send an enquiry to The Art Lounge Nirvana about acquiring an artwork.
          </DialogDescription>
          <div className="grid md:grid-cols-2">
            {/* Visual side */}
            <div className="relative hidden md:block bg-sand overflow-hidden">
              {artwork && !artwork.is_service ? (
                <img
                  src={artwork.image}
                  alt={artwork.title}
                  className="h-full w-full object-cover"
                />
              ) : (
                <div className="h-full w-full flex flex-col items-start justify-end p-10 bg-gradient-to-br from-sand to-paper">
                  {artwork?.is_service && (
                    <span className="font-body text-[10px] tracking-[0.3em] uppercase text-ochre mb-4">
                      The Art Lounge Nirvana
                    </span>
                  )}
                  <span className="font-serif italic text-3xl md:text-4xl text-ink-soft leading-[1.1]">
                    {artwork?.is_service ? (
                      <>Let&apos;s begin<br />the conversation.</>
                    ) : (
                      <>Begin a<br />conversation</>
                    )}
                  </span>
                </div>
              )}
              <div className="absolute inset-0 bg-ink/10 pointer-events-none" />
            </div>

            {/* Form side */}
            <div className="p-8 sm:p-10">
              <p className="font-body text-[11px] tracking-[0.3em] uppercase text-ochre mb-3">
                {artwork?.is_service ? "Studio Enquiry" : artwork ? "Enquire to Acquire" : "Studio Enquiry"}
              </p>
              <h3 className="font-serif text-3xl leading-tight text-ink">
                {artwork ? artwork.title : "Get in touch"}
              </h3>
              {artwork && !artwork.is_service && (
                <p className="font-body text-sm text-ink-soft mt-1">
                  {artwork.medium} &middot; {artwork.dimensions}
                  {!artwork.price_on_request && (
                    <span className="text-ink"> &middot; {artwork.price}</span>
                  )}
                </p>
              )}
              {artwork?.is_service && artwork?.tagline && (
                <p className="font-serif italic text-base text-ink-soft mt-2">
                  {artwork.tagline}
                </p>
              )}

              <AnimatePresence mode="wait">
                {status === "done" ? (
                  <motion.div
                    key="done"
                    initial={{ opacity: 0, y: 12 }}
                    animate={{ opacity: 1, y: 0 }}
                    className="mt-8 flex flex-col items-start gap-4"
                    data-testid="enquiry-success"
                  >
                    <span className="flex h-12 w-12 items-center justify-center bg-ochre text-paper">
                      <Check className="h-6 w-6" />
                    </span>
                    <p className="font-serif text-2xl text-ink leading-snug">
                      Thank you, {form.name.split(" ")[0] || "friend"}.
                    </p>
                    <p className="font-body text-sm text-ink-soft">
                      Your enquiry has reached The Art Lounge Nirvana. Aparna's
                      studio will respond personally, usually within a day or two.
                    </p>
                    <button
                      type="button"
                      onClick={() => setOpen(false)}
                      data-testid="enquiry-close-button"
                      className="mt-2 border border-ink px-6 py-3 font-body text-xs tracking-[0.2em] uppercase text-ink transition-colors hover:bg-ink hover:text-paper"
                    >
                      Close
                    </button>
                  </motion.div>
                ) : (
                  <motion.form
                    key="form"
                    initial={{ opacity: 0 }}
                    animate={{ opacity: 1 }}
                    onSubmit={handleSubmit}
                    className="mt-6 space-y-5"
                  >
                    {[
                      { k: "name", label: "Your name", type: "text", ph: "Full name", testid: "enquiry-name-input" },
                      { k: "email", label: "Email", type: "email", ph: "you@email.com", testid: "enquiry-email-input" },
                      { k: "phone", label: "Phone (optional)", type: "tel", ph: "+91 \u2026", testid: "enquiry-phone-input" },
                    ].map((f) => (
                      <div key={f.k}>
                        <label className="block font-body text-[11px] tracking-[0.2em] uppercase text-ink-soft mb-2">
                          {f.label}
                        </label>
                        <input
                          type={f.type}
                          value={form[f.k]}
                          onChange={change(f.k)}
                          placeholder={f.ph}
                          data-testid={f.testid}
                          className="w-full bg-transparent border-b border-ink-soft/40 py-2 font-body text-sm text-ink placeholder:text-ink-soft/50 focus:outline-none focus:border-ochre transition-colors"
                        />
                      </div>
                    ))}
                    <div>
                      <label className="block font-body text-[11px] tracking-[0.2em] uppercase text-ink-soft mb-2">
                        Message
                      </label>
                      <textarea
                        rows={3}
                        value={form.message}
                        onChange={change("message")}
                        placeholder={artwork ? `I'd love to know more about "${artwork.title}"\u2026` : "How can the studio help?"}
                        data-testid="enquiry-message-input"
                        className="w-full bg-transparent border-b border-ink-soft/40 py-2 font-body text-sm text-ink placeholder:text-ink-soft/50 focus:outline-none focus:border-ochre transition-colors resize-none"
                      />
                    </div>
                    <button
                      type="submit"
                      disabled={status === "sending"}
                      data-testid="enquiry-submit-button"
                      className="group inline-flex items-center gap-3 bg-ink px-8 py-4 font-body text-xs tracking-[0.25em] uppercase text-paper transition-colors hover:bg-ochre disabled:opacity-60"
                    >
                      {status === "sending" ? (
                        <><Loader2 className="h-4 w-4 animate-spin" /> Sending</>
                      ) : (
                        <>Send enquiry <span className="transition-transform group-hover:translate-x-1">&rarr;</span></>
                      )}
                    </button>
                  </motion.form>
                )}
              </AnimatePresence>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </EnquiryContext.Provider>
  );
}
